import { useCallback, useEffect, useRef, useState } from 'react'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { Panel } from '@/card/record'
import { ApiError } from '@/lib/api'
import {
  SUMMARY_SECTIONS,
  fetchLatestSummary,
  fetchSummary,
  requestSummary,
  type ConversationSummary,
  type SummarySource,
  type SummarySubject,
} from './summaryApi'

/**
 * Резюме переговоров (тикет #130): кнопка «Резюме», период, состояние задачи словами и сводка
 * тремя разделами, где у каждого утверждения — ссылки на записи, из которых оно взято.
 *
 * Состояние держится в самом компоненте, а не в кеше запросов: панель живёт и в ленте звонка,
 * которую рисуют экраны без своего клиента запросов, и опрос задачи — два таймера, а не
 * подписка.
 *
 * Утверждения без настоящей ссылки сервер кладёт отдельно (`unsupported`) — здесь они и
 * показаны отдельно, под предупреждением, а не среди подтверждённых.
 */

const WORKING_POLL_MS = 2000
const RETRY_POLL_MS = 30000

/** Право пользоваться функциями ИИ: без него кнопку не показываем — сервер всё равно откажет. */
export function useMaySummarise(): boolean {
  const { user } = useApp()
  return can(user, 'ai.assistant', 'use')
}

export function useConversationSummary(subject: SummarySubject, id: string, loadLatest: boolean) {
  const { t } = useApp()
  const [summary, setSummary] = useState<ConversationSummary | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const alive = useRef(true)

  const message = useCallback(
    (failure: unknown) => (failure instanceof ApiError ? failure.message : t('app.error')),
    [t],
  )

  const follow = useCallback(
    (current: ConversationSummary) => {
      if (timer.current) clearTimeout(timer.current)
      if (current.state !== 'working' && current.state !== 'retrying') return
      timer.current = setTimeout(
        () => {
          fetchSummary(current.taskId)
            .then((next) => {
              if (!alive.current) return
              setSummary(next)
              follow(next)
            })
            .catch((failure: unknown) => alive.current && setError(message(failure)))
        },
        current.state === 'working' ? WORKING_POLL_MS : RETRY_POLL_MS,
      )
    },
    [message],
  )

  useEffect(() => {
    alive.current = true
    return () => {
      alive.current = false
      if (timer.current) clearTimeout(timer.current)
    }
  }, [])

  useEffect(() => {
    if (!loadLatest) return
    fetchLatestSummary(subject, id)
      .then((latest) => {
        if (!alive.current || !latest) return
        setSummary(latest)
        follow(latest)
      })
      // The latest summary is a convenience: failing to read it must not block the button.
      .catch(() => undefined)
  }, [subject, id, loadLatest, follow])

  const start = useCallback(
    (from?: string, to?: string) => {
      setBusy(true)
      setError(null)
      requestSummary({ entityType: subject, entityId: id, from, to })
        .then((created) => {
          if (!alive.current) return
          setSummary(created)
          follow(created)
        })
        .catch((failure: unknown) => alive.current && setError(message(failure)))
        .finally(() => alive.current && setBusy(false))
    },
    [subject, id, follow, message],
  )

  return { summary, error, busy, start }
}

function isoDay(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

/** Панель на карточке сделки или клиента: период и кнопка «Резюме». */
export function ConversationSummaryPanel({ entityType, entityId }: { entityType: 'deal' | 'counterparty'; entityId: string }) {
  const { t } = useApp()
  const may = useMaySummarise()
  const { summary, error, busy, start } = useConversationSummary(entityType, entityId, may)
  const [to, setTo] = useState(() => isoDay(new Date()))
  const [from, setFrom] = useState(() => isoDay(new Date(Date.now() - 90 * 86_400_000)))
  if (!may) return null
  const working = summary?.state === 'working' || summary?.state === 'retrying'
  return (
    <Panel
      title={t('summary.title')}
      actions={
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          disabled={busy || working}
          onClick={() => start(from, to)}
        >
          {summary ? t('summary.again') : t('summary.button')}
        </button>
      }
    >
      <div className="stack" style={{ gap: 8 }}>
        <p className="t-sm muted" style={{ margin: 0 }}>{t('summary.intro')}</p>
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          <label className="row" style={{ gap: 4 }}>
            <span className="t-sm">{t('summary.from')}</span>
            <input type="date" className="input" value={from} max={to} onChange={(event) => setFrom(event.target.value)} />
          </label>
          <label className="row" style={{ gap: 4 }}>
            <span className="t-sm">{t('summary.to')}</span>
            <input type="date" className="input" value={to} min={from} onChange={(event) => setTo(event.target.value)} />
          </label>
        </div>
        <SummaryBody summary={summary} error={error} />
      </div>
    </Panel>
  )
}

/** Состояние, разделы и «без подтверждения» — общее для сделки, клиента и звонка. */
export function SummaryBody({ summary, error }: { summary: ConversationSummary | null; error: string | null }) {
  const { t, locale } = useApp()
  const [hint, setHint] = useState<string | null>(null)
  if (error) {
    return <p role="alert" className="t-sm">{error}</p>
  }
  if (!summary) return null
  if (summary.state === 'working') {
    return <p role="status" className="t-sm muted">{t('summary.state.working')}</p>
  }
  if (summary.state === 'retrying') {
    return <p role="status" className="t-sm">{summary.message ?? t('summary.state.working')}</p>
  }
  if (summary.state === 'failed') {
    return (
      <p role="alert" className="t-sm">
        {t('summary.state.failed')}{summary.message ? `: ${summary.message}` : ''}
      </p>
    )
  }

  const reveal = (source: SummarySource) => {
    const element = typeof document === 'undefined' ? null : document.getElementById(`feed-event-${source.recordId}`)
    if (element) {
      setHint(null)
      element.scrollIntoView?.({ behavior: 'smooth', block: 'center' })
      element.classList.add('is-highlighted')
      setTimeout(() => element.classList.remove('is-highlighted'), 2000)
    } else {
      setHint(t('summary.notInFeed'))
    }
  }

  const unsupported = summary.unsupported ?? []
  return (
    <div className="stack" style={{ gap: 10 }}>
      {SUMMARY_SECTIONS.map((section) => {
        const statements = summary.sections?.[section] ?? []
        return (
          <section key={section} aria-label={t(`summary.section.${section}`)} className="stack" style={{ gap: 4 }}>
            <span className="strong">{t(`summary.section.${section}`)}</span>
            {statements.length === 0 ? (
              <span className="t-sm muted">{t('summary.section.empty')}</span>
            ) : (
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {statements.map((statement, index) => (
                  <li key={`${section}-${index}`}>
                    <span>{statement.text}</span>{' '}
                    {statement.sources.map((source) => (
                      <SourceLink key={`${source.label}`} source={source} locale={locale} onReveal={reveal} />
                    ))}
                    {statement.unknownSources && statement.unknownSources.length > 0 && (
                      <span className="t-sm muted"> ({t('summary.unknownLabels', { labels: statement.unknownSources.join(', ') })})</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>
        )
      })}
      {hint && <p className="t-sm muted" role="status">{hint}</p>}
      {unsupported.length > 0 && (
        <section aria-label={t('summary.unsupported.title')} className="stack" style={{ gap: 4 }}>
          <span className="strong">{t('summary.unsupported.title')}</span>
          <span className="t-sm muted">{t('summary.unsupported.hint')}</span>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {unsupported.map((item, index) => (
              <li key={`unsupported-${index}`} className="t-sm">
                {item.text} —{' '}
                {item.reason === 'unknown_source'
                  ? t('summary.unsupported.unknown_source', { labels: (item.citedLabels ?? []).join(', ') })
                  : t('summary.unsupported.no_source')}
              </li>
            ))}
          </ul>
        </section>
      )}
      <span className="t-sm muted">
        {t('summary.sources', { count: summary.sourceCount })}
        {summary.omittedSources > 0 ? ` · ${t('summary.omitted', { count: summary.omittedSources })}` : ''}
      </span>
    </div>
  )
}

function clock(millis: number): string {
  const seconds = Math.max(0, Math.floor(millis / 1000))
  return `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`
}

/** Ссылка-источник: вид записи и когда; по нажатию лента прокручивается к ней. */
function SourceLink({
  source,
  locale,
  onReveal,
}: {
  source: SummarySource
  locale: string
  onReveal: (source: SummarySource) => void
}) {
  const { t } = useApp()
  const when = source.at
    ? new Date(source.at).toLocaleDateString(locale === 'en' ? 'en-GB' : 'ru-RU', { day: '2-digit', month: '2-digit' })
    : ''
  const label = source.offsetMs != null
    ? t('summary.fragment', { clock: clock(source.offsetMs) })
    : `${t(`timeline.kind.${source.kind}`)}${when ? ` ${when}` : ''}`
  const unheard = source.kind === 'call' && source.transcribed === false
  return (
    <a
      href={`#feed-event-${source.recordId}`}
      className="chip"
      title={source.title ?? undefined}
      data-record-id={source.recordId}
      onClick={(event) => {
        event.preventDefault()
        onReveal(source)
      }}
      style={{ marginRight: 4 }}
    >
      {label}
      {unheard ? ` · ${t('summary.untranscribed')}` : ''}
    </a>
  )
}
